/**
 * AdminUserPermissionsModal — alt admin (subadmin) yetki atama/düzenleme modalı.
 *
 * Adım 1: personel seçici (role employee/manager olanlar; autocomplete arama).
 * Adım 2: modül ağacı — Sidebar grup/buton yapısıyla aynı anahtarlar
 *         (adminModuleMap.ADMIN_MODULE_GROUPS; grup toggle + tekil buton checkbox'ları).
 * Kaydet: updatePersonnel(uid,{role:'subadmin'}) + setAdminPanel(uid,true,settings).
 *
 * Şablon: branches/ManagerPermissionsModal.tsx (managerPanel deseni).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPersonnel,
  getAdminPanel,
  setAdminPanel,
  updatePersonnel,
  type Personnel,
} from '../../services/migration/personnelDataService';
import {
  ADMIN_MODULE_GROUPS,
  type AdminPermissionsMap,
} from './adminModuleMap';

interface AdminUserPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  /** Kaydetme başarılı olunca çağrılır (liste yenileme). */
  onSaved: () => void;
  /** Düzenleme modu: mevcut alt adminin uid'i. null/undefined = yeni kullanıcı ekleme. */
  editUid?: string | null;
}

/** Seçim durumu: { [sectionId]: Set<itemId> } yerine düz harita tutulur. */
type SelectionState = Record<string, Record<string, boolean>>;

function emptySelection(): SelectionState {
  const out: SelectionState = {};
  for (const group of ADMIN_MODULE_GROUPS) {
    out[group.id] = {};
    for (const item of group.items) out[group.id][item.id] = false;
  }
  return out;
}

function selectionFromPermissions(perms: AdminPermissionsMap | null): SelectionState {
  const out = emptySelection();
  if (!perms) return out;
  for (const group of ADMIN_MODULE_GROUPS) {
    const section = perms[group.id];
    if (!section || section.enabled !== true) continue;
    for (const item of group.items) {
      out[group.id][item.id] = section.items?.[item.id] === true;
    }
  }
  return out;
}

function selectionToPermissions(sel: SelectionState): AdminPermissionsMap {
  const out: AdminPermissionsMap = {};
  for (const group of ADMIN_MODULE_GROUPS) {
    const items: Record<string, boolean> = {};
    let any = false;
    for (const item of group.items) {
      const on = sel[group.id]?.[item.id] === true;
      items[item.id] = on;
      if (on) any = true;
    }
    out[group.id] = { enabled: any, items };
  }
  return out;
}

const AdminUserPermissionsModal: React.FC<AdminUserPermissionsModalProps> = ({
  open,
  onClose,
  onSaved,
  editUid,
}) => {
  const { t, i18n } = useTranslation();
  const { userDetails } = useAuth();
  const isTr = i18n.language.startsWith('tr');

  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [selectedUser, setSelectedUser] = useState<Personnel | null>(null);
  const [selection, setSelection] = useState<SelectionState>(emptySelection);
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const isEdit = Boolean(editUid);

  // Personel listesini yükle (modal açılınca).
  useEffect(() => {
    if (!open || !userDetails?.companyId) return;
    let cancelled = false;
    setLoading(true);
    getPersonnel(userDetails.companyId)
      .then((list) => {
        if (cancelled) return;
        setPersonnel(list.filter((p) => !p.isDeleted));
        if (editUid) {
          const target = list.find((p) => p.id === editUid) || null;
          setSelectedUser(target);
        }
      })
      .catch((err) => {
        console.error('[AdminUserPermissionsModal] Personel yüklenemedi:', err);
        if (!cancelled) setSnackbar({ message: t('users.messages.loadError'), severity: 'error' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editUid, userDetails?.companyId]);

  // Seçilen kullanıcının mevcut izinlerini yükle.
  useEffect(() => {
    if (!open || !selectedUser) {
      setSelection(emptySelection());
      return;
    }
    let cancelled = false;
    setLoadingPerms(true);
    getAdminPanel(selectedUser.id)
      .then((res) => {
        if (cancelled) return;
        setSelection(
          selectionFromPermissions(
            res.found && res.settings && typeof res.settings === 'object' ? res.settings : null
          )
        );
      })
      .catch(() => {
        if (!cancelled) setSelection(emptySelection());
      })
      .finally(() => {
        if (!cancelled) setLoadingPerms(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedUser]);

  // Yeni kullanıcı adayları: employee/manager (admin, subadmin, kurye hariç).
  const candidates = useMemo(
    () =>
      personnel.filter(
        (p) => p.role === 'employee' || p.role === 'manager'
      ),
    [personnel]
  );

  const handleToggleGroup = (groupId: string) => {
    setOpenGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleItemChange = (groupId: string, itemId: string, checked: boolean) => {
    setSelection((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], [itemId]: checked },
    }));
  };

  const handleGroupToggleAll = (groupId: string, checked: boolean) => {
    setSelection((prev) => {
      const next = { ...prev, [groupId]: { ...prev[groupId] } };
      const group = ADMIN_MODULE_GROUPS.find((g) => g.id === groupId);
      for (const item of group?.items || []) next[groupId][item.id] = checked;
      return next;
    });
  };

  const selectedCount = useMemo(() => {
    let n = 0;
    for (const group of ADMIN_MODULE_GROUPS) {
      for (const item of group.items) {
        if (selection[group.id]?.[item.id]) n++;
      }
    }
    return n;
  }, [selection]);

  const handleSave = async () => {
    if (!selectedUser) return;
    if (selectedCount === 0) {
      setSnackbar({ message: t('users.messages.noModuleSelected'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      // 1) İzin haritasını kaydet (CompanyAdminOnly korumalı uç).
      await setAdminPanel(selectedUser.id, true, selectionToPermissions(selection));
      // 2) Rolü subadmin yap (zaten subadmin ise atla).
      if (selectedUser.role !== 'subadmin') {
        await updatePersonnel(selectedUser.id, { role: 'subadmin' });
      }
      setSnackbar({ message: t('users.messages.saveSuccess'), severity: 'success' });
      setTimeout(() => {
        onSaved();
        handleClose();
      }, 800);
    } catch (err: any) {
      console.error('[AdminUserPermissionsModal] Kaydetme hatası:', err);
      setSnackbar({
        message: err?.response?.data?.message || err?.message || t('users.messages.saveError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSelection(emptySelection());
    setOpenGroups([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              {isEdit ? t('users.modal.editTitle') : t('users.modal.addTitle')}
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('users.modal.description')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Adım 1: personel seçici */}
        {isEdit ? (
          selectedUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {(selectedUser.name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography fontWeight={600}>{selectedUser.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUser.email}
                </Typography>
              </Box>
              <Chip size="small" label={t('users.roleSubAdmin')} color="primary" variant="outlined" />
            </Box>
          )
        ) : (
          <Autocomplete
            options={candidates}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            loading={loading}
            getOptionLabel={(o) => `${o.name || ''} (${o.email || ''})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Avatar sx={{ width: 28, height: 28, mr: 1.5, bgcolor: 'primary.main', fontSize: 14 }}>
                  {(option.name || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email} — {option.role}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('users.modal.selectPersonnel')}
                placeholder={t('users.modal.selectPersonnelHint')}
              />
            )}
            sx={{ mb: 3 }}
          />
        )}

        {/* Adım 2: modül ağacı */}
        {selectedUser ? (
          loadingPerms ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">{t('users.modal.modulePermissions')}</Typography>
                {selectedCount > 0 && (
                  <Chip size="small" color="primary" label={t('users.modal.selectedCount', { count: selectedCount })} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('users.modal.modulePermissionsHint')}
              </Typography>

              <List component="div" sx={{ width: '100%' }}>
                {ADMIN_MODULE_GROUPS.map((group) => {
                  const groupOpen = openGroups.includes(group.id);
                  const itemStates = group.items.map((item) => selection[group.id]?.[item.id] === true);
                  const enabledCount = itemStates.filter(Boolean).length;
                  const allChecked = enabledCount === group.items.length && group.items.length > 0;
                  const someChecked = enabledCount > 0 && !allChecked;
                  return (
                    <Box
                      key={group.id}
                      sx={{ mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
                    >
                      <ListItem
                        onClick={() => handleToggleGroup(group.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: 'rgba(16, 38, 72, 0.06)',
                          '&:hover': { bgcolor: 'rgba(16, 38, 72, 0.1)' },
                        }}
                        secondaryAction={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Grup toggle: tümünü seç/temizle */}
                            <Checkbox
                              checked={allChecked}
                              indeterminate={someChecked}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(_, checked) => handleGroupToggleAll(group.id, checked)}
                            />
                            <IconButton
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleGroup(group.id);
                              }}
                            >
                              {groupOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {isTr ? group.labelTr : group.labelEn}
                              </Typography>
                              {enabledCount > 0 && (
                                <Chip size="small" color="primary" label={enabledCount} sx={{ height: 20 }} />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>

                      <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                        {group.items.map((item) => {
                          const checked = selection[group.id]?.[item.id] === true;
                          return (
                            <ListItem
                              key={`${group.id}-${item.id}`}
                              sx={{
                                pl: 4,
                                borderTop: 1,
                                borderColor: 'divider',
                                bgcolor: checked ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                              }}
                              secondaryAction={
                                <Checkbox
                                  checked={checked}
                                  onChange={(_, c) => handleItemChange(group.id, item.id, c)}
                                />
                              }
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle2" fontWeight={500}>
                                    {isTr ? item.labelTr : item.labelEn}
                                  </Typography>
                                }
                                secondary={item.paths[0]}
                              />
                            </ListItem>
                          );
                        })}
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </Box>
          )
        ) : (
          <Alert severity="info">{t('users.modal.selectPersonnelFirst')}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {t('common.cancel', { defaultValue: 'İptal' })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!selectedUser || saving || loadingPerms}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {saving ? t('users.modal.saving') : t('users.modal.save')}
        </Button>
      </DialogActions>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity || 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AdminUserPermissionsModal;
